import type {FluentVariable} from '@fluent/bundle';
import {UPSELL_LADDER, upsellCandidates} from 'config';
import {Plan} from 'types';

/**
 * What a blocked run needs from a plan, in the same units the plan limits use.
 * Every field is optional — only the dimensions the run actually tripped are
 * asked for, so a wall with no measurable size (a boolean feature, a per-period
 * request quota) simply passes an empty requirement and gets the next tier.
 */
export type PlanRequirement = {
    /** Longest single text / document, in characters → words_per_generation_limit. */
    chars?: number;
    /** Words the run will DEBIT — already multiplied by the scan-type price
     *  (AI ×1, plagiarism ×2, grammar ×1) → granted_words_simple_scan. */
    words?: number;
    /** Files in the batch → batch_doc_upload. */
    files?: number;
    /** Humanizer words the run will debit (input word count) → granted_words_humanizer. */
    humanizerWords?: number;
};

export type Dimension = keyof PlanRequirement;

/** The plan limit each dimension is measured against. */
const DIMENSION_FIELD: Record<Dimension, string> = {
    chars: 'words_per_generation_limit',
    words: 'granted_words_simple_scan',
    files: 'batch_doc_upload',
    humanizerWords: 'granted_words_humanizer',
};

/** The buyer's word position, which decides how much of a plan's grant is actually available. */
export type WordWallet = {
    /** Words already spent this period (`used_words_simple_scan`, holds included). */
    usedWords: number;
    /** Humanizer words already spent this period (limits.humanizer_words.used, holds included). */
    usedHumanizerWords?: number;
    /** Pay-as-you-go words granted on top of the plan (`additional_granted_words_simple_scan`). */
    extraWords?: number;
    /**
     * True when the purchase MODIFIES an existing subscription rather than creating one.
     * The backend re-anchors the limit cycle (and so resets usage) only on the first webhook
     * of a NEW Stripe subscription — a plan change reuses the record and skips the reset — so
     * an upgrade inherits the words already spent and only the REMAINDER of the new grant is
     * available. See `_re_anchor_limit_cycle` in app/bitapi/payments/views.py.
     */
    isUpgrade: boolean;
};

type PlanLimits = {[field: string]: any};

/** Plan grants exposed by GET /payments/prices, keyed by plan. */
export type PlansLimits = {[plan: string]: PlanLimits};

/** A plan's monthly word grant. `words_from` is the fallback for a plan quoted only as a floor —
 *  Enterprise used to be, and a live /payments/prices response still may be. */
export const wordGrant = (limits: PlanLimits): number =>
    limits.granted_words_simple_scan ?? limits.words_from ?? 0;

/** The numbers paywall copy quotes about one plan, formatted for the fluent args. */
export const planCopyArgs = (limits: PlanLimits = {}) => ({
    wordsK: (wordGrant(limits) / 1000).toLocaleString(),
    charsK: ((limits[DIMENSION_FIELD.chars] ?? 0) / 1000).toLocaleString(),
    files: (limits[DIMENSION_FIELD.files] ?? 0).toLocaleString(),
    humanizerWords: (limits[DIMENSION_FIELD.humanizerWords] ?? 0).toLocaleString(),
});

export type PlanFeatureId = 'words' | 'chars' | 'files' | 'deepScans' | 'plagiarism' | 'humanizer';
export type PlanFeature = {id: PlanFeatureId; text: string};

/**
 * What one plan gets you, in the order both paywalls list it (Figma 329:14090: the scan
 * quotas first, then the checks; the Humanizer, which the design predates, last). Shared so
 * the two can't drift apart — they already had: the same five bullets, two spellings of the
 * plagiarism one, and a "Big files support" line that promised nothing (the per-file cap is
 * equal on every plan). `id` is for the surface that draws an icon per feature.
 * `humanizer` adds the Humanizer words line — the caller passes UserStore.humanizerAvailable.
 */
export const planFeatureBullets = (
    t: (key: string, args?: Record<string, FluentVariable>) => string,
    limits: PlanLimits,
    humanizer: boolean,
): PlanFeature[] => {
    const {wordsK, charsK, files, humanizerWords} = planCopyArgs(limits);
    return [
        {id: 'words', text: t('paywall-feature-words', {wordsK})},
        {id: 'chars', text: t('paywall-feature-chars', {charsK})},
        {id: 'files', text: t('paywall-feature-batch-docs', {files})},
        {id: 'deepScans', text: t('paywall-feature-deep-scans')},
        {id: 'plagiarism', text: t('paywall-feature-plagiarism')},
        ...(humanizer ? [{id: 'humanizer' as const, text: t('paywall-feature-humanizer', {humanizerWords})}] : []),
    ];
};

/** The one upgrade rule for every consumable dimension: a plan change keeps the
 *  period, so the spend carries over and only the remainder is available. */
const netted = (grant: number, spent: number, wallet: WordWallet): number =>
    wallet.isUpgrade ? Math.max(0, grant - spent) : grant;

/** What the buyer has already consumed of a dimension, when it is consumable at all
 *  ('words' has its own path through availableWordsOn, which also counts extra words). */
const DIMENSION_SPEND: Partial<Record<Dimension, (wallet: WordWallet) => number>> = {
    humanizerWords: wallet => wallet.usedHumanizerWords ?? 0,
};

/** How many words this plan would actually leave the buyer to spend right now. */
export const availableWordsOn = (limits: PlanLimits, wallet: WordWallet): number =>
    netted(wordGrant(limits) + (wallet.extraWords ?? 0), wallet.usedWords, wallet);

/** What one plan offers for one dimension — consumable ones net off what is already spent. */
const planOffers = (limits: PlanLimits | undefined, dimension: Dimension, wallet: WordWallet): number => {
    if (!limits) return 0;
    if (dimension === 'words') return availableWordsOn(limits, wallet);
    const value = limits[DIMENSION_FIELD[dimension]];
    if (typeof value !== 'number') return 0;
    const spend = DIMENSION_SPEND[dimension];
    return spend ? netted(value, spend(wallet), wallet) : value;
};

/** Does this plan cover every dimension the run needs? A dimension the plan doesn't
 *  quote (missing field) can't be verified, so it is treated as not covered. */
export const planCovers = (limits: PlanLimits | undefined, req: PlanRequirement, wallet: WordWallet): boolean =>
    (Object.keys(DIMENSION_FIELD) as Dimension[])
        .every(d => req[d] === undefined || planOffers(limits, d, wallet) >= req[d]!);

/**
 * The cheapest plan above `current` that actually covers the blocked run — the plan the
 * paywall may honestly promise will let it through.
 *
 * Selling the NEXT tier by position was the bug this replaces: a free user pasting 250k
 * characters was sold Premium (100k cap), paid, and hit the very same wall. So the ladder is
 * walked in price order and the first plan that satisfies EVERY tripped dimension wins;
 * `null` means no plan does, and the caller must not sell one (the modal falls back to the
 * free "continue" options + sales contact, naming the wall via uncoveredDimension).
 *
 * With an empty requirement this degrades to exactly `getNextPlan`: the next tier up.
 */
export const pickCoveringPlan = (
    current: Plan | null,
    req: PlanRequirement,
    plansLimits: PlansLimits,
    wallet: WordWallet,
): Plan | null => {
    const candidates = upsellCandidates(current);
    // Nothing measurable to satisfy (a boolean feature, a per-period request quota): any paid
    // plan lifts it, so keep the funnel's next tier rather than reading limits that don't apply.
    if (!(Object.keys(DIMENSION_FIELD) as Dimension[]).some(d => req[d] !== undefined)) {
        return candidates[0] ?? null;
    }
    return candidates.find(plan => planCovers(plansLimits[plan], req, wallet)) ?? null;
};

/**
 * Which dimension of the run no plan can satisfy, and the ceiling we could actually sell —
 * what the "no plan covers this" copy quotes.
 *
 * Judged with the SAME arithmetic pickCoveringPlan used (words net off what is already spent
 * on an upgrade), so the message can never contradict the decision that produced it.
 * `null` when every dimension is coverable — i.e. the buyer is simply on the top tier.
 *
 * Because of that netting, `best` on the `words` dimension is what the buyer could still spend,
 * NOT what a plan grants — the copy must say "can cover right now", never "includes".
 */
export const uncoveredDimension = (
    req: PlanRequirement,
    plansLimits: PlansLimits,
    wallet: WordWallet,
): {dimension: Dimension, needed: number, best: number} | null => {
    // Deliberate order: the message names the hardest wall first (a text too
    // long for any plan beats a spent allowance).
    for (const dimension of ['chars', 'files', 'words', 'humanizerWords'] as Dimension[]) {
        const needed = req[dimension];
        if (needed === undefined) continue;
        const best = bestOffer(plansLimits, dimension, wallet);
        if (needed > best) return {dimension, needed, best};
    }
    return null;
};

/** The most any sellable plan offers for one dimension. */
const bestOffer = (plansLimits: PlansLimits, dimension: Dimension, wallet: WordWallet): number =>
    Math.max(0, ...UPSELL_LADDER.map(plan => planOffers(plansLimits[plan], dimension, wallet)));

/** The ceiling of every plan for one dimension, ignoring what the buyer has already spent. */
export const bestPlanLimit = (plansLimits: PlansLimits, dimension: Dimension): number =>
    bestOffer(plansLimits, dimension, {usedWords: 0, isUpgrade: false});
